import React, { useLayoutEffect } from 'react';
import { Creator } from './creator/Creator';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import { MessageDataPayload, MessageDataTitle, UUID } from '@/types/types';
import { Anchor, Flex, Modal, Text, Image } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { setLoading } from '@/store/slices/appSlice';
import { useContent } from '@/providers/ContentProvider';
import ProjectApi from '@/api/ProjectApi';
import PresetApi from '@/api/PresetApi';
import styles from './App.module.scss';
import Logo from '@/assets/images/logo.png';

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);

export const isDev = !!searchParams.get('DEV') || import.meta.env.DEV;
export const lockedCollectionId = searchParams.get('collection');
export const isPresetMode = !!searchParams.get('presetMode');
export const projectId = searchParams.get('project') as UUID | null;
export const presetId = searchParams.get('preset');
export const storeId = searchParams.get('storeId') as UUID;

const baseSlug = 'app.';

const App: React.FC = () => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { options } = useTypedSelector((app) => app.creator);

	const modalControls = {
		open: () => {
			dispatch(creatorActions.updateOptions({ isOpen: true }));
		},
		close: () => {
			dispatch(creatorActions.updateOptions({ isOpen: false }));
		},
	};

	const loadPresetMutation = useMutation({
		mutationFn: PresetApi.getWithoutStore,
		onSuccess: (preset) => {
			dispatch(creatorActions.loadPreset(preset));
			dispatch(setLoading(false));
		},
	});

	const loadCreatorMutation = useMutation({
		mutationFn: ProjectApi.loadCreatorData,
		onMutate: () => {
			dispatch(
				creatorActions.updateOptions({
					isOpen: true,
					isPreviewMode: true,
				})
			);
		},
		onSuccess: (data) => {
			dispatch(setLoading(false));
			dispatch(creatorActions.enterPreviewMode(data));
		},
	});

	useLayoutEffect(() => {
		window.onmessage = ({ data }: MessageEvent<MessageDataPayload>) => {
			switch (data.title) {
				case MessageDataTitle.TOGGLE:
					data.isOpen ? modalControls.open() : modalControls.close();
					break;
			}
		};

		if (presetId) {
			dispatch(setLoading(true));
			loadPresetMutation.mutate({ presetId: presetId as UUID, storeId });
		}

		if (projectId) {
			dispatch(setLoading(true));
			loadCreatorMutation.mutate(projectId as UUID);
			return;
		}

		if (isPresetMode) {
			if (presetId) {
				dispatch(setLoading(true));
				loadPresetMutation.mutate({ presetId: presetId as UUID, storeId });
			} else
				dispatch(
					creatorActions.updateOptions({
						isPresetMode,
					})
				);
			return;
		}

		if (presetId)
			dispatch(
				creatorActions.updateOptions({
					lockedPresetId: presetId as UUID,
				})
			);

		if (lockedCollectionId)
			dispatch(
				creatorActions.updateOptions({
					lockedCollectionId: lockedCollectionId as UUID,
				})
			);
	}, []);

	return (
		<Modal
			fullScreen
			opened={options.isOpen}
			title={
				<Flex
					align="center"
					justify="space-between"
					direction={{
						base: 'column',
						md: 'row',
					}}
				>
					<Image src={Logo} w={200} />
					<Text className="creator-title" size="xl" fw="bold">
						{getContent(`${baseSlug}title`)}
					</Text>
					<Anchor onClick={modalControls.close}>
						{getContent(`${baseSlug}backToShop`)}
					</Anchor>
				</Flex>
			}
			onClose={modalControls.close}
			transitionProps={{
				duration: 250,
				transition: 'fade',
				onExited: () => {
					window!.top!.postMessage(
						{
							title: MessageDataTitle.TOGGLE,
							isOpen: false,
						},
						'*'
					);
				},
			}}
			classNames={{
				title: styles.modalTitle,
				header: styles.modalHeader,
				content: styles.modalContent,
				body: styles.modalBody,
			}}
		>
			<Creator handleClose={modalControls.close} />
		</Modal>
	);
};

export default App;
